import { axiosClient } from '../../../library/Service/axiosClient';

const BASE = () => `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/vibrancy`;

export interface ProfileMetric {
    label: string;
    value: string;
    sub:   string;
    color: string;
    prev:  string;
    delta: string;
}

export interface ProfileOverviewData {
    instituteName: string;
    periodLabel:   string;
    totalAlumni:   number;
    metrics:       ProfileMetric[];
}

export interface BatchRow {
    passOutYear:     number;
    batchSize:       number;
    backEndProfiled: number;
    frontEndActive:  number;
}

export interface LocationData {
    indiaCount:   number;
    abroadCount:  number;
    indiaPct:     number;
    abroadPct:    number;
    totalLocated: number;
}

export interface StudentCoverageData {
    activeStudents:     number;
    profileCompletePct: number;
}

export interface LocationAndStudentData {
    location:       LocationData;
    studentCoverage: StudentCoverageData | null;
}

export const VibrancyProfileService = {
    getProfileOverview: (instituteId: string | number, instituteName: string, period: string) =>
        axiosClient().get<{ success: boolean; data: ProfileOverviewData }>(`${BASE()}/profile-overview`, {
            params: { institute_id: instituteId, institute_name: instituteName, period },
        }),

    /**
     * GET /vibrancy/batch-bifurcation
     *   ?institute_id=X&program_id=Y&batch_start_year=Z&batch_end_year=W
     * program_id, batch_start_year, batch_end_year are optional.
     */
    /** GET /vibrancy/outreach-summary?institute_id=X&period=monthly */
    getOutreachSummary: (instituteId: string | number, period: string) =>
        axiosClient().get<{ success: boolean; data: {
            totalAttempts: number; alumniReached: number; successful: number;
            followUp: number; closed: number; coveragePct: number;
            disposition: { successfulPct: number; followUpPct: number; noRespPct: number };
            purposes: { category: string; count: number; color: string; width: number }[];
        } }>(`${BASE()}/outreach-summary`, { params: { institute_id: instituteId, period } }),

    /** GET /vibrancy/notable-alumni?institute_id=X&limit=3 */
    getNotableAlumni: (instituteId: string | number, limit = 3) =>
        axiosClient().get<{ success: boolean; data: {
            studentId: string; name: string; passOutYear: number | null;
            city: string | null; country: string | null; designation: string | null; companyName: string | null;
            latestInstitution: string | null; profilePicture: string | null;
        }[] }>(`${BASE()}/notable-alumni`, { params: { institute_id: instituteId, limit } }),

    /** GET /vibrancy/academic-counts?institute_id=X — prestigious total + scholarship total + lists */
    getAcademicCounts: (instituteId: string | number) =>
        axiosClient().get<{ success: boolean; data: {
            prestigious:  number;
            scholarships: number;
            prestigiousList: { instituteName: string; alumniCount: number; width: number }[];
            scholarshipList: { name: string; passOutYear: number | null; level: string; scholarshipType: string | null; amount: number | null; currency: string | null; collegeName: string | null }[];
        } }>(`${BASE()}/academic-counts`, { params: { institute_id: instituteId } }),

    /** GET /vibrancy/top-intl-colleges?institute_id=X&limit=5 */
    getTopIntlColleges: (instituteId: string | number, limit = 5) =>
        axiosClient().get<{ success: boolean; data: {
            ugAbroad: { universityId: number; name: string; alumniCount: number }[];
            pgAbroad: { universityId: number; name: string; alumniCount: number }[];
        } }>(`${BASE()}/top-intl-colleges`, { params: { institute_id: instituteId, limit } }),

    /** GET /vibrancy/top-colleges?institute_id=X&limit=5 */
    getTopColleges: (instituteId: string | number, limit = 5) =>
        axiosClient().get<{ success: boolean; data: {
            ugIndian: { universityId: number; name: string; alumniCount: number }[];
            pgIndian: { universityId: number; name: string; alumniCount: number }[];
        } }>(`${BASE()}/top-colleges`, { params: { institute_id: instituteId, limit } }),

    /** GET /vibrancy/top-ug-courses?institute_id=X&limit=5 */
    getTopUgCourses: (instituteId: string | number, limit = 5) =>
        axiosClient().get<{ success: boolean; data: { code: string; label: string; alumniCount: number; pct: number; width: number }[] }>(
            `${BASE()}/top-ug-courses`,
            { params: { institute_id: instituteId, limit } },
        ),

    /** GET /vibrancy/commitments?institute_id=X&period=monthly */
    getCommitmentsData: (instituteId: string | number, period: string) =>
        axiosClient().get<{ success: boolean; data: {
            thisPeriod:     number;
            ytd:            number;
            conversionRate: number;
            focused:    { goalCode: string; icon: string; name: string; total: number; confirmed: number; followUp: number; declined: number }[];
            notInFocus: { goalCode: string; icon: string; name: string }[];
        } }>(`${BASE()}/commitments`, { params: { institute_id: instituteId, period } }),

    /** GET /vibrancy/location-student?institute_id=X */
    getLocationAndStudent: (instituteId: string | number) =>
        axiosClient().get<{ success: boolean; data: LocationAndStudentData }>(`${BASE()}/location-student`, {
            params: { institute_id: instituteId },
        }),

    getBatchBifurcation: (
        instituteId:    string | number,
        programId?:     number | null,
        batchStartYear?: number | null,
        batchEndYear?:   number | null,
    ) =>
        axiosClient().get<{ success: boolean; data: BatchRow[] }>(`${BASE()}/batch-bifurcation`, {
            params: {
                institute_id:     instituteId,
                program_id:       programId     || undefined,
                batch_start_year: batchStartYear || undefined,
                batch_end_year:   batchEndYear   || undefined,
            },
        }),
};
